<script>
import AddSLA from './AddSLA.vue';
import SLAEmptyState from './components/SLAEmptyState.vue';
import SLAListItem from './components/SLAListItem.vue';
import SLAListItemLoading from './components/SLAListItemLoading.vue';
import SLAPaywallEnterprise from './components/SLAPaywallEnterprise.vue';

import { mapGetters } from 'vuex';
import { convertSecondsToTimeUnit } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';

export default {
  components: {
    AddSLA,
    SLAEmptyState,
    SLAListItem,
    SLAListItemLoading,
    SLAPaywallEnterprise,
  },
  data() {
    return {
      loading: {},
      showAddPopup: false,
      showDeleteConfirmationPopup: false,
      selectedResponse: {},
      searchQuery: '',
    };
  },
  computed: {
    ...mapGetters({
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      records: 'sla/getSLA',
      currentUser: 'getCurrentUser',
      accountId: 'getCurrentAccountId',
      uiFlags: 'sla/getUIFlags',
    }),
    filteredRecords() {
      if (!this.searchQuery) return this.records;
      const query = this.searchQuery.toLowerCase();
      return this.records.filter(
        sla =>
          sla.name.toLowerCase().includes(query) ||
          (sla.description && sla.description.toLowerCase().includes(query))
      );
    },
    deleteConfirmText() {
      return this.$t('SLA.DELETE.CONFIRM.YES');
    },
    deleteRejectText() {
      return this.$t('SLA.DELETE.CONFIRM.NO');
    },
    deleteMessage() {
      return ` ${this.selectedResponse.name}`;
    },
    isBehindAPaywall() {
      return !this.isFeatureEnabledonAccount(this.accountId, 'sla');
    },
    isSuperAdmin() {
      return this.currentUser.type === 'SuperAdmin';
    },
  },
  mounted() {
    this.$store.dispatch('sla/get');
  },
  methods: {
    openAddPopup() {
      if (this.isBehindAPaywall) {
        return;
      }
      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedResponse = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    confirmDeletion() {
      this.loading[this.selectedResponse.id] = true;
      this.closeDeletePopup();
      this.deleteSla(this.selectedResponse.id);
    },
    deleteSla(id) {
      this.$store
        .dispatch('sla/delete', id)
        .then(() => {
          useAlert(this.$t('SLA.DELETE.API.SUCCESS_MESSAGE'));
        })
        .catch(() => {
          useAlert(this.$t('SLA.DELETE.API.ERROR_MESSAGE'));
        })
        .finally(() => {
          this.loading[this.selectedResponse.id] = false;
        });
    },
    displayTime(threshold) {
      const { time, unit } = convertSecondsToTimeUnit(threshold, {
        minute: 'm',
        hour: 'h',
        day: 'd',
      });
      if (!time) return '-';
      return `${time}${unit}`;
    },
    onClickCTA() {
      this.$router.push({
        name: 'billing_settings_index',
        params: { accountId: this.accountId },
      });
    },
  },
};
</script>

<template>
  <div class="settings-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="ws-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 50px"
    >
      <h1 class="text-xl font-semibold" style="color: #111827">
        {{ $t('SLA.HEADER') }}
      </h1>
      <button
        class="primary-action-btn"
        :disabled="isBehindAPaywall"
        @click="openAddPopup"
      >
        <span class="i-ph-plus-bold size-4" />
        {{ $t('SLA.ADD.TITLE') }}
      </button>
    </header>

    <!-- Description -->
    <div class="ws-desc-bar px-6 py-3">
      <p class="text-sm" style="color: #4b5563">
        {{ $t('SLA.DESCRIPTION') }}
      </p>
    </div>

    <!-- Paywall -->
    <SLAPaywallEnterprise
      v-if="isBehindAPaywall"
      :is-super-admin="isSuperAdmin"
      :is-on-wlab-cloud="isOnChatwootCloud"
      class="flex-1"
      @upgrade="onClickCTA"
    />

    <template v-else>
      <!-- Search and Filters -->
      <div class="ws-toolbar flex items-center gap-4 px-6 py-3">
        <div class="flex items-center gap-4">
          <div class="relative flex-1 max-w-xs">
            <span
              class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 pointer-events-none"
              style="color: #9ca3af"
            />
            <input
              v-model="searchQuery"
              type="text"
              class="ws-search reset-base w-full pl-10 pr-4 py-2.5 text-sm rounded-lg"
              placeholder="Search SLA policies..."
            />
          </div>
          <div class="text-sm self-center" style="color: #6b7280">
            {{ filteredRecords.length }} policies
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="ws-content flex-1 overflow-y-auto p-6">
        <!-- Loading State -->
        <div v-if="uiFlags.isFetching" class="space-y-3">
          <SLAListItemLoading v-for="ii in 2" :key="ii" />
        </div>

        <!-- Empty State -->
        <SLAEmptyState
          v-else-if="!filteredRecords.length"
          @primary-action="openAddPopup"
        />

        <!-- SLA List -->
        <div v-else class="flex flex-col w-full h-full gap-3">
          <SLAListItem
            v-for="sla in filteredRecords"
            :key="sla.title"
            :sla-name="sla.name"
            :description="sla.description"
            :first-response="displayTime(sla.first_response_time_threshold)"
            :next-response="displayTime(sla.next_response_time_threshold)"
            :resolution-time="displayTime(sla.resolution_time_threshold)"
            :has-business-hours="sla.only_during_business_hours"
            :is-loading="loading[sla.id]"
            @delete="openDeletePopup(sla)"
          />
        </div>
      </div>
    </template>

    <!-- Modals -->
    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddSLA @close="hideAddPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('SLA.DELETE.CONFIRM.TITLE')"
      :message="$t('SLA.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </div>
</template>

<style scoped>
.settings-page,
.dark .settings-page {
  background: #ffffff !important;
}
.ws-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-desc-bar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-toolbar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}
.ws-content {
  background: #ffffff;
}
.ws-search {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}
.ws-search:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  background: #fff !important;
}
.ws-search::placeholder {
  color: #9ca3af !important;
}
.primary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Excon', system-ui, sans-serif;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.primary-action-btn:hover {
  background: #1f2937;
}
.primary-action-btn:active {
  transform: scale(0.98);
}
.primary-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.bg-n-solid-1),
:deep(.bg-n-background) {
  background: #ffffff !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}

/* SLAListItem white-theme override */
:deep(.sla-item),
:deep([class*='sla']) {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
  color: #111827 !important;
}
:deep(.sla-item *),
:deep([class*='sla'] *) {
  color: #111827 !important;
}
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2),
:deep(.bg-n-solid-3) {
  background: #ffffff !important;
}
:deep(.border-n-weak),
:deep(.border-n-strong) {
  border-color: #e5e7eb !important;
}
:deep(.text-n-slate-9),
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.bg-n-alpha-1),
:deep(.bg-n-alpha-2) {
  background: #f9fafb !important;
}

:deep(.wlab-card-modern),
:deep(.sla-item),
:deep(.outline-n-container) {
  background: #ffffff !important;
  outline-color: #e5e7eb !important;
  border-color: #e5e7eb !important;
}

:deep(.wlab-card-modern:hover),
:deep(.sla-item:hover) {
  outline-color: #d1d5db !important;
}

:deep(.sm\:divide-n-weak > :not([hidden]) ~ :not([hidden])) {
  border-color: #e5e7eb !important;
}

/* Force add-SLA modal light */
:deep(.modal-mask .modal-container) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}

:deep(.modal-mask .modal-header),
:deep(.modal-mask .modal-content),
:deep(.modal-mask form) {
  background: #ffffff !important;
}
</style>
